// Share-with-friends card for the wallet sheet. Each user gets one
// personal referral code; when a friend signs up via the link, both
// sides receive +30 minutes of free trial time in their wallet
// (handled by the accept-referral edge function).
import { useEffect, useState } from "react";
import { Copy, Check, Gift, Share2, Send, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

function randomCode() {
  // 8-char base36, uppercased — short and easy to type.
  return Array.from(crypto.getRandomValues(new Uint8Array(6)))
    .map((b) => b.toString(36))
    .join("")
    .replace(/[^a-z0-9]/gi, "")
    .slice(0, 8)
    .toUpperCase();
}

export default function ShareWithFriends() {
  const { user } = useAuth();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      // Reuse an existing personal invite if there is one.
      const { data: existing } = await supabase
        .from("referrals")
        .select("code")
        .eq("referrer_user_id", user.id)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (existing?.code) {
        setCode(existing.code);
        setLoading(false);
        return;
      }
      // Otherwise mint one. Retry a couple of times in case of unique conflict.
      for (let attempt = 0; attempt < 4; attempt++) {
        const c = randomCode();
        const { error } = await supabase
          .from("referrals")
          .insert({ referrer_user_id: user.id, code: c });
        if (!error) {
          if (!cancelled) {
            setCode(c);
            setLoading(false);
          }
          return;
        }
        if (!error.message?.toLowerCase().includes("duplicate")) break;
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) return null;

  const link = code ? `${window.location.origin}/signup?ref=${code}` : "";
  const shareText = "I'm using HackerLab.ai — sign up with my link and we both get +30 min of free AI time.";

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const nativeShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "HackerLab.ai", text: shareText, url: link });
      } catch {
        /* user cancelled */
      }
    } else {
      copy();
    }
  };

  const tweetHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `${shareText} ${link}`,
  )}`;
  const mailHref = `mailto:?subject=${encodeURIComponent(
    "30 minutes of free AI on HackerLab.ai",
  )}&body=${encodeURIComponent(`${shareText}\n\n${link}`)}`;

  return (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 grid place-items-center rounded-lg bg-primary/15 text-primary shrink-0">
          <Gift className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Share with friends — get 30 min more</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            When a friend signs up with your link, you both get +30 minutes added to your wallet.
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-stretch gap-2">
        <input
          readOnly
          value={loading ? "Generating your link…" : link || "Couldn't generate link"}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 min-w-0 h-9 px-2 rounded-md border border-border bg-background text-xs font-mono truncate"
        />
        <Button size="sm" variant="outline" onClick={copy} disabled={!link} className="shrink-0">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span className="ml-1.5">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" onClick={nativeShare} disabled={!link} className="h-8">
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
        </Button>
        <Button asChild size="sm" variant="outline" disabled={!link} className="h-8">
          <a href={tweetHref} target="_blank" rel="noreferrer">
            <Send className="w-3.5 h-3.5 mr-1.5" /> Tweet
          </a>
        </Button>
        <Button asChild size="sm" variant="outline" disabled={!link} className="h-8">
          <a href={mailHref}>
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Email
          </a>
        </Button>
      </div>
    </div>
  );
}